import React, {FC, useContext, useEffect} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Box, Button, Typography} from "@material-ui/core";
import {useDispatch, useSelector} from "react-redux";
import {
  goToCardDetails,
  goToCart,
  selectBillingDetails,
  updateBillingDetails,
} from "store/exhibitors/cartStepper/slice";
import {BillingDetails} from "store/exhibitors/cartStepper/helper";
import SettingsTable from "view/components/SettingsList";
import {LocalMapContext} from "view/context/LocalMapContext";
import {makeLowerCase} from "tools/show/localMap";
import {
  LocalMapType,
  BrandingType,
  IExhibitorPair,
  IExhibitorUser,
  IShow,
} from "@evennt-tech/api-v2-core";

import {generateTestId} from "tools/cypress/generateTestId";
import {
  useIsShowDiscountCodesInvalid,
  useShowDiscountCodesActions,
} from "store/discount-code/discount-code-store";
import useBranding from "store/branding/branding.store";
import {
  areBillingDetailsComplete,
  deliveryDetailsSettings,
  exhibitorDetailsSettings,
  resolveBillingDetails,
} from "./helpers";
import {useStyles} from "../style";

type DetailsProps = {
  show: IShow;
  exhibitorPair: IExhibitorPair;
  exhibitorUser: IExhibitorUser;
  categoryIdsBySupplier: Record<string, Set<string>>;
};

const Details: FC<DetailsProps> = ({
  show,
  exhibitorPair,
  exhibitorUser,
  categoryIdsBySupplier,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();

  const localMap = useContext(LocalMapContext);

  const dispatch = useDispatch();
  const billingDetails = useSelector(selectBillingDetails);
  const hasInvalidDiscountCodes = useIsShowDiscountCodesInvalid(show.id);
  const {validate: validateDiscountCodes} = useShowDiscountCodesActions();

  const disabledButton =
    !areBillingDetailsComplete(billingDetails) || hasInvalidDiscountCodes;

  useEffect(() => {
    if (!billingDetails.tradingName) {
      setBillingDetails();
    }
  }, []);

  const setBillingDetails = () => {
    dispatch(
      updateBillingDetails(
        resolveBillingDetails(billingDetails, exhibitorPair, exhibitorUser)
      )
    );
  };

  if (!show) {
    return null;
  }

  const onBillingDetailsUpdate = (
    billingDetailsUpdate: Partial<BillingDetails>
  ) => {
    dispatch(updateBillingDetails(billingDetailsUpdate));
  };
  const standText = makeLowerCase(localMap.stand, LocalMapType.Stand);

  const {brandings} = useBranding();
  const branding = brandings[show.id];

  return (
    <Box className={classes.root}>
      <Box
        className={classes.contentContainer}
        style={{
          maxHeight:
            branding?.backgroundType === BrandingType.IMAGE ? "470px" : "unset",
        }}
      >
        <Typography variant="h4" className={classes.helperText}>
          <T
            keyName="exhibitor.cart.details.review_info"
            params={{standText}}
          />
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          <T keyName="exhibitor.cart.details.required_info" />
        </Typography>
        <Box className={classes.details}>
          <SettingsTable
            withoutPaper
            className={classes.contrastText}
            settings={exhibitorDetailsSettings(
              billingDetails,
              show,
              localMap,
              t
            )}
            onUpdate={onBillingDetailsUpdate}
            values={billingDetails}
            title=""
            editTitle
          />
          <Box className={classes.delivery}>
            <SettingsTable
              withoutPaper
              settings={deliveryDetailsSettings(
                exhibitorPair,
                show,
                localMap,
                t
              )}
              onUpdate={() => null}
              values={exhibitorPair}
              title={t("exhibitor.cart.details.delivery_details_title")}
            />
          </Box>
        </Box>
      </Box>
      <Box className={classes.buttons} pt={2}>
        <Button
          {...generateTestId("exhibitor-cart-details-back-to-cart-btn")}
          className={classes.buttonBackToCart}
          color="primary"
          onClick={() => dispatch(goToCart())}
        >
          <T keyName="exhibitor.cart.details.back_to_cart" />
        </Button>
        <Button
          {...generateTestId("exhibitor-cart-details-next-btn")}
          disabled={disabledButton}
          classes={{text: classes.buttonText}}
          color="primary"
          variant="contained"
          onClick={async () => {
            const isValidCodes = await validateDiscountCodes(
              show.id,
              exhibitorPair.standId,
              categoryIdsBySupplier
            );
            if (!isValidCodes) {
              return;
            }
            if (!disabledButton) {
              dispatch(goToCardDetails());
            }

            setBillingDetails();
          }}
        >
          <T keyName="common.action.next" />
        </Button>
      </Box>
    </Box>
  );
};

export default Details;
